import type { AvatarConfig } from "@shared/types";

export const DRAW_PROTOCOL_VERSION = 1;

export type DrawPhase = "lobby" | "choosing" | "drawing" | "reveal" | "podium";
export type NextDrawerMode = "random" | "host-picks";

export interface DrawPlayer {
  id: string;
  displayName: string;
  avatarConfig: AvatarConfig;
  score: number;
  lastDelta: number;
  guessedCorrect: boolean;
  online: boolean;
  /** Has drawn at least once this game — powers the "who's up next" tracker. */
  hasDrawn: boolean;
}

export interface DrawSettings {
  packIds: string[];
  /** Seconds per round. 0 = no timer (host ends it manually). */
  roundTimeSec: number;
  roundCount: number;
  nextDrawerMode: NextDrawerMode;
}

export interface StrokePoint { x: number; y: number }
export interface Stroke {
  points: StrokePoint[];
  color: string;
  width: number;
}

export interface DrawRoomState {
  code: string;
  hostId: string;
  phase: DrawPhase;
  players: DrawPlayer[];
  settings: DrawSettings;
  roundIndex: number;
  drawerId?: string;
  /** Only present on the drawer's own device (server strips it for everyone
   *  else — see DrawRoom.stateFor). */
  wordOptions?: string[];
  wordLength?: number;
  roundStartedAt?: number;
  strokes: Stroke[];
  /** Only present once phase is "reveal"/"podium" — safe to show then. */
  revealedWord?: string;
  /** Shuffled tile bank for the guess keyboard: the word's letters + 2-3
   *  decoys. Safe to broadcast — order is shuffled. */
  letterBank?: string[];
  rev?: number;
  maxPlayers?: number;
}

export type DrawEvent =
  | { type: "state"; state: DrawRoomState }
  | { type: "error"; message: string }
  /** Live stroke relay — NOT folded into `state` (too high-frequency);
   *  consumers should draw these directly onto a canvas ref. */
  | { type: "stroke-point"; x: number; y: number; newStroke: boolean; color?: string; width?: number }
  | { type: "clear" }
  /** Reply to MY OWN guess only (never broadcast to others). */
  | { type: "guess-feedback"; correct: boolean; delta?: number; text?: string }
  /** Someone else guessed correctly — no word reveal, just a nudge. */
  | { type: "correct-announce"; playerId: string; displayName: string };

export interface DrawJoinOpts {
  code: string;
  role: "host" | "player";
  me: { id: string; displayName: string; avatarConfig: AvatarConfig };
  settings?: Partial<DrawSettings>;
}

export interface DrawClient {
  join(opts: DrawJoinOpts): Promise<DrawRoomState>;
  setSettings(settings: Partial<DrawSettings>): void;
  /** Host only: begin the game with the resolved word pool (plain English
   *  words from the selected packs). */
  start(pool: string[]): void;
  backToLobby(): void;
  /** Drawer only: pick one of the 3 offered words. */
  chooseWord(index: number): void;
  /** Drawer only: stream one point of the current stroke. */
  strokePoint(x: number, y: number, newStroke: boolean, color?: string, width?: number): void;
  /** Drawer only: wipe the canvas mid-round. */
  clearCanvas(): void;
  /** Drawer only: remove the last stroke. */
  undoStroke(): void;
  /** Guesser: submit a guess attempt. */
  guess(text: string): void;
  /** Host only: force the round to reveal right now. */
  endRoundNow(): void;
  /** Host only: advance from reveal to the next round (optionally naming the
   *  next drawer, when settings.nextDrawerMode is "host-picks"). */
  nextRound(drawerId?: string): void;
  on(cb: (e: DrawEvent) => void): () => void;
  leave(): void;
}
